import { useEffect, useState, useRef, type FormEvent } from 'react';

// Standalone, read-only inspector portal rendered for /i/<token>. No owner login,
// no app navigation — only what the grant permits. Every load is logged server-side.

type Meta = { ok: boolean; label?: string; needPin?: boolean; scopes?: string[]; reason?: string };
type Result = { ok: boolean; needPin?: boolean; reason?: string; label?: string; scopes?: string[]; data?: Record<string, unknown> };

const wrap: React.CSSProperties = { minHeight: '100vh', background: 'var(--bg)', padding: '24px 16px' };
const sheet: React.CSSProperties = { maxWidth: 920, margin: '0 auto' };

export function Inspector() {
  const token = decodeURIComponent(window.location.pathname.replace(/^\/i\//, ''));
  const [meta, setMeta] = useState<Meta | null>(null);
  const [pin, setPin] = useState('');
  const [res, setRes] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`/api/inspect/${token}/meta`).then((r) => r.json()).then((m: Meta) => {
      setMeta(m);
      if (m.ok && !m.needPin) load(undefined);
    }).catch(() => setMeta({ ok: false, reason: 'Could not reach the server.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(p: string | undefined, e?: FormEvent) {
    e?.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r: Result = await fetch(`/api/inspect/${token}`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ pin: p }),
      }).then((x) => x.json());
      if (r.ok) setRes(r);
      else if (r.needPin) { setMeta((m) => ({ ...(m ?? { ok: true }), ok: true, needPin: true, label: r.label ?? m?.label })); setErr(r.reason ?? null); }
      else setErr(r.reason ?? 'Access denied.');
    } catch { setErr('Could not reach the server.'); } finally { setBusy(false); }
  }

  if (!meta) return <div style={wrap}><div style={sheet} className="muted">Loading…</div></div>;

  if (!meta.ok && !res) {
    return <div style={wrap}><div style={sheet}><Header /><div className="card card-pad" style={{ color: 'var(--bad)' }}>{meta.reason}</div></div></div>;
  }

  if (meta.needPin && !res) {
    return (
      <div style={wrap}><div style={{ ...sheet, maxWidth: 400 }}>
        <Header />
        <form className="card card-pad" onSubmit={(e) => load(pin, e)}>
          <h3 style={{ marginTop: 0 }}>{meta.label ?? 'Secure record'}</h3>
          <p className="muted" style={{ fontSize: 13 }}>This disclosure is PIN-protected. Enter the PIN you were given.</p>
          <input className="fld" style={{ width: '100%', padding: 10, border: '1px solid var(--border2)', borderRadius: 8 }}
            type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN" autoFocus />
          {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, margin: '6px 0' }}>{err}</div>}
          <button className="btn btn-primary" type="submit" disabled={busy} style={{ marginTop: 8, width: '100%', justifyContent: 'center' }}>{busy ? 'Checking…' : 'View records'}</button>
        </form>
      </div></div>
    );
  }

  if (res?.ok) return <div style={wrap}><div style={sheet}><Header label={res.label} /><Data data={res.data ?? {}} scopes={res.scopes ?? []} /><EvidentiaryStatement /></div></div>;
  return <div style={wrap}><div style={sheet}><Header /><div className="card card-pad" style={{ color: 'var(--bad)' }}>{err}</div></div></div>;
}

function Header({ label }: { label?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
      <div className="brand-mark">S</div>
      <div>
        <div style={{ fontWeight: 600 }}>SOR — Inspector view</div>
        <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{label ? `Disclosure to: ${label}` : 'Read-only disclosure'}</div>
      </div>
    </div>
  );
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function Data({ data, scopes }: { data: Record<string, any>; scopes: string[] }) {
  const isFullFinancial = scopes.includes('financial-full') || !!(data.accounts?.accounts?.[0]?.account_number);
  return (
    <>
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          This is a read-only, scoped disclosure from a tamper-evident compliance record. Each record is
          hash-chained and timestamped into Bitcoin; this view was logged. You are seeing only the
          categories the record-holder chose to share.
        </p>
      </div>

      {data.devices && (
        <Section title="Devices" hint={`${data.devices.length} on record`}>
          {data.devices.map((d: any) => (
            <Row key={d.uuid} badge={d.status?.toUpperCase()} kind={['disposed','sold','lost','stolen','returned'].includes(d.status) ? 'warn' : 'ok'}
              main={<><strong>{d.label}</strong>{(d.make || d.model) ? <span className="muted"> · {[d.make,d.model].filter(Boolean).join(' ')}</span> : null}{d.serial ? <span className="mono muted3"> · {d.serial}</span> : null}</>}
              sub={`${d.category ?? ''}${d.acquired_date ? ' · acquired ' + d.acquired_date : ''}`} />
          ))}
        </Section>
      )}

      {data.notifications && (
        <Section title="Notifications to police" hint={`${data.notifications.length}`}>
          {data.notifications.map((n: any) => (
            <Row key={n.uuid} badge={n.rule_id ?? 'NOTIFY'} kind="ok"
              main={<strong>{n.what}</strong>} sub={`told police ${n.notified_date}${n.method ? ' · ' + n.method : ''}`} />
          ))}
        </Section>
      )}

      {data.reconciliation?.buckets && (
        <Section title="Police-list reconciliation" hint={data.reconciliation.snapshot ? `as held ${data.reconciliation.snapshot.snapshot_date}` : ''}>
          <Row badge={String(data.reconciliation.buckets.counts.stale_open)} kind="warn" main={<strong>On the police list, no longer held</strong>} sub="reported disposals/sales the police list hasn’t caught up with" />
          <Row badge={String(data.reconciliation.buckets.counts.matched)} kind="ok" main={<strong>Matched</strong>} sub="on both your list and the police list" />
          {data.reconciliation.buckets.on_police_not_yours.map((s: any) => (
            <Row key={s.item_id} badge={s.resolution ? s.resolution.resolution.toUpperCase() : 'STALE'} kind={s.resolution ? 'ok' : 'warn'}
              main={<span style={{ textTransform: 'capitalize' }}>{s.police}</span>}
              sub={s.resolution ? `resolved ${s.resolution.resolution}${s.resolution.notified_date ? ' · police notified ' + s.resolution.notified_date : ''}` : 'police still hold this'} />
          ))}
        </Section>
      )}

      {data.accounts && !isFullFinancial && (
        <Section title="Bank accounts & cards" hint="masked">
          {data.accounts.accounts.map((a: any) => (
            <Row key={a.uuid} badge={a.status?.toUpperCase()} kind="ok" main={<strong>{a.institution}</strong>} sub={`${a.account_type ?? 'account'}${a.last4 ? ' ••' + a.last4 : ''}`} />
          ))}
          {data.accounts.cards.map((c: any) => (
            <Row key={c.uuid} badge="CARD" kind="ok" main={<strong>{c.brand ?? 'Card'} ••{c.pan_last4}</strong>} sub={c.card_type ?? ''} />
          ))}
        </Section>
      )}

      {data.accounts && isFullFinancial && (
        <Section title="Financial details (full)" hint="unmasked — handle with care">
          {data.accounts.accounts.map((a: any) => {
            const linkedCards = data.accounts.cards.filter((c: any) => c.account_uuid === a.uuid);
            return (
              <div key={a.uuid} style={{ borderBottom: '1px solid var(--border)', padding: '10px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  {a.status && <span className={`badge badge-ok`}>{a.status.toUpperCase()}</span>}
                  <strong>{a.institution}</strong>
                  {a.account_type && <span className="muted" style={{ fontSize: 12 }}>{a.account_type}</span>}
                </div>
                <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink2)', marginBottom: 2 }}>
                  {a.account_number && <span>Account: <strong>{a.account_number}</strong></span>}
                  {a.sort_code && <span style={{ marginLeft: 12 }}>Sort code: <strong>{a.sort_code}</strong></span>}
                </div>
                {(a.account_holder_name || a.issuing_branch) && (
                  <div className="ledger-time">{[a.account_holder_name, a.issuing_branch].filter(Boolean).join(' · ')}</div>
                )}
                {linkedCards.length > 0 && linkedCards.map((c: any) => (
                  <div key={c.uuid} style={{ marginTop: 6, paddingLeft: 12, borderLeft: '3px solid var(--border2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="badge badge-ok">CARD</span>
                      <span className="mono" style={{ fontSize: 12.5 }}><strong>{c.pan ?? c.brand ?? 'Card'}</strong></span>
                      {c.expiry && <span className="muted" style={{ fontSize: 12 }}>exp {c.expiry}</span>}
                      {c.card_type && <span className="muted" style={{ fontSize: 12 }}>{c.card_type}</span>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {data.accounts.cards.filter((c: any) => !c.account_uuid).map((c: any) => (
            <Row key={c.uuid} badge="CARD" kind="ok"
              main={<span className="mono"><strong>{c.pan ?? c.brand ?? 'Card'}</strong>{c.expiry ? <span className="muted" style={{ fontSize: 12 }}> · exp {c.expiry}</span> : null}</span>}
              sub={c.card_type ?? ''} />
          ))}
        </Section>
      )}
    </>
  );
}

function EvidentiaryStatement() {
  const [text, setText] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const fetched = useRef(false);

  function toggle() {
    if (!fetched.current) {
      fetched.current = true;
      fetch('/api/inspect/evidentiary')
        .then((r) => r.text())
        .then((t) => setText(t))
        .catch(() => setText('Could not load the evidentiary statement.'));
    }
    setOpen((o) => !o);
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="panel-head" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggle}>
        <h3>How this record is verified (for your solicitor)</h3>
        <span className="hint">{open ? 'hide ▲' : 'show ▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '12px 16px' }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--ink2)' }}>
            A full cryptographic proof packet (hash chain, Bitcoin anchor, and audit log) can be provided
            for independent verification upon request.
          </p>
          {text === null ? (
            <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12.5, lineHeight: 1.6,
              background: 'var(--bg)', border: '1px solid var(--border2)', borderRadius: 6,
              padding: '12px 14px', margin: 0, color: 'var(--ink)' }}>
              {text}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="panel-head"><h3>{title}</h3>{hint && <span className="hint">{hint}</span>}</div>
      <div className="ledger">{children}</div>
    </div>
  );
}
function Row({ badge, kind, main, sub }: { badge?: string; kind: string; main: React.ReactNode; sub?: string }) {
  return (
    <div className="ledger-row">
      {badge && <span className={`badge badge-${kind}`}>{badge}</span>}
      <div className="ledger-main"><div className="ledger-text">{main}</div>{sub && <div className="ledger-time">{sub}</div>}</div>
    </div>
  );
}
